import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { vestResolver } from "@hookform/resolvers/vest";
import { merge } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field";
import {
  EmailEditorTab,
  EmailEditorTabs,
  useEmailEditorTabs
} from "components/email-builder";
import { EMAIL_BUILDER_MERGE_TAGS } from "components/email-builder/email-builder-merge-tags";
import ErrorSnackbarContent from "components/error-snackbar-content";
import FileUpload from "components/file-upload";
import FileUploadComputerAndEDocs from "components/file-upload-with-edocs";
import DisplayUploadSelectedFiles from "components/file-upload/display-upload-selected-files.component";
import InputFormField from "components/input-form-field";
import ModalForm from "components/modal-form";
import WordProcessorField from "components/word-processor-field";
import {
  APPLY_DEFAULT_EMAIL_TEMPLATE,
  DRAG_AND_DROP_EMAIL_BUILDER,
  UPLOAD_FILES_FROM_EDOCS_FEATURE
} from "constants/feature-flags";
import {
  EMAIL_SUBJECT_MAX_LENGTH,
  EMAIL_TEMPLATE_DESIGN_ONLY_TYPES,
  EMAIL_TEMPLATE_WITH_DESIGN_TYPES,
  TEMPLATE_TYPE_DESIGNER_EMAIL
} from "constants/templates";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useGetClinicQuery } from "services/clinics";
import {
  useAttachFilesToEmailsMutation,
  useSendEmailMutation
} from "services/emails";
import { PATIENT_DATA_SOURCE_QUERY } from "services/merge-tags";
import { useGetPatientQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { type TextTemplate } from "services/types/text-templates.type";
import { useGetUserQuery } from "services/users";
import { getTextTemplateSubjectValue } from "utils/message-templates.util";
import { MAX_FILE_SIZE } from "utils/validations.utils";

import {
  setCustomMessage,
  setOpen
} from "../action-snackbar/action-snackbar-slice";
import PatientFieldWithSearch from "../patient-field-with-search";
import {
  getMergeTagValuesMap,
  replaceMergeTagsWithValues
} from "./email-form-merge-tags.utils";
import { emailFormValidator } from "./email-form-validator";
import {
  formatRecipient,
  getDefaultSubject,
  getDefaultValueFromPatient,
  getEmailRequestBody,
  normalizeMergeTagsForSend
} from "./email.utils";
import RecipientsField, { Recipient } from "./recipients-field.component";

type EmailFormProps = {
  patient?: Patient;
  isOpen: boolean;
  onClose: () => void;
  selectedFiles?: any[];
  setSelectedFiles?: React.Dispatch<React.SetStateAction<any[]>>;
  initialDocuments?: File[];
  initialEmailContent?: string;
  initialSubject?: string;
  noSignature?: boolean;
  /** When set, opens with Drag & Drop Email Builder tab active and loads this design (e.g. for Send Preview from template) */
  initialEmailDesignJson?: UnlayerDesign | null;
  /** When true, opens with Drag & Drop Email Builder tab active even when no initial design (e.g. Send Preview with empty template) */
  openWithEmailBuilderTab?: boolean;
};

export interface EmailFormValues {
  recipients: (Recipient | string)[];
  subject: string;
  content: string;
  files: File[];
  patient: Patient | null;
  isUseBackendEmailTemplate: boolean;
}

const DEFAULT_VALUES: EmailFormValues = {
  recipients: [],
  subject: "",
  content: "",
  files: [],
  patient: null,
  isUseBackendEmailTemplate: false
};

export default function EmailForm({
  patient,
  isOpen,
  onClose,
  initialDocuments,
  selectedFiles,
  setSelectedFiles,
  initialEmailContent,
  initialSubject,
  noSignature,
  initialEmailDesignJson,
  openWithEmailBuilderTab = false
}: EmailFormProps) {
  const params = useParams();
  const flags = useFlags();
  const isApplyDefaultEmailTemplateEnabled =
    flags[APPLY_DEFAULT_EMAIL_TEMPLATE];
  const isUploadFilesFromEDocsEnabled = flags[UPLOAD_FILES_FROM_EDOCS_FEATURE];
  const isDragAndDropEmailEnabled = flags[DRAG_AND_DROP_EMAIL_BUILDER];
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);

  const { data: user } = useGetUserQuery(currentUser.id, {
    skip: !currentUser.id || currentUser.id === -1
  });

  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(
    null
  );

  const [subjectChanged, setSubjectChanged] = useState(false);

  // Email builder tabs state
  const {
    emailBuilderRef,
    activeTab,
    setActiveTab,
    isEmailBuilder,
    exportHtml,
    loadDesign,
    setActiveDesign,
    activeDesign,
    editorTabIsDisabled,
    setEditorTabIsDisabled,
    reset: resetEmailBuilder
  } = useEmailEditorTabs();

  useEffect(() => {
    if (patient) {
      setSelectedRecipient(formatRecipient(patient));
    } else {
      setSelectedRecipient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isTemplateUsed, setIsTemplateUsed] = useState(false);

  const [uploadWarningMessage, setUploadWarningMessage] = useState("");

  const defaultValues: EmailFormValues = useMemo(() => {
    if (!isOpen) return DEFAULT_VALUES;

    const emailSignature = user?.user_preferences?.email_signature || "";

    return merge(
      {},
      DEFAULT_VALUES,
      getDefaultValueFromPatient({
        initialData: {
          files: initialDocuments,
          content: initialEmailContent,
          subject: initialSubject
        },
        patient,
        currentClinic: currentClinic,
        userFullName: currentUser.fullName,
        emailSignature,
        noSignature
      })
    );
  }, [
    isOpen,
    patient,
    currentClinic,
    initialEmailContent,
    currentUser.fullName,
    initialDocuments,
    initialSubject,
    noSignature,
    user?.user_preferences?.email_signature
  ]);

  const [sendEmail, { isLoading: sendEmailIsLoading }] = useSendEmailMutation();
  const [attachEmailFiles, { isLoading: attachEmailFilesIsLoading }] =
    useAttachFilesToEmailsMutation();

  const isLoading = sendEmailIsLoading || attachEmailFilesIsLoading;

  const formMethods = useForm<EmailFormValues>({
    mode: "onBlur",
    resolver: vestResolver(emailFormValidator(), undefined, {
      mode: "sync"
    }),
    defaultValues
  });
  const { control, reset, setValue, handleSubmit, watch } = formMethods;
  const currentPatient: any = watch("patient");

  const handleOnTemplateInserted = useCallback(
    (template: TextTemplate | null) => {
      if (template && !isTemplateUsed) {
        setIsTemplateUsed(true);
      }

      const subject = getTextTemplateSubjectValue(template?.subject_template);
      if (subject) {
        setValue("subject", subject, {
          shouldDirty: true,
          shouldValidate: true
        });
        setSubjectChanged(true);
      }

      if (
        template?.type === TEMPLATE_TYPE_DESIGNER_EMAIL &&
        template?.email_design_json
      ) {
        loadDesign(template.email_design_json);
      }
    },
    [isTemplateUsed, loadDesign, setValue]
  );

  const patientId =
    currentPatient?.id === -1
      ? selectedRecipient?.id || undefined
      : currentPatient?.id ||
        selectedRecipient?.id ||
        Number(
          (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
        ) ||
        undefined;

  const { data: fetchedPatientForMerge } = useGetPatientQuery(
    { patientId: patientId!, query: PATIENT_DATA_SOURCE_QUERY },
    { skip: !patientId || patientId <= 0 }
  );

  const { data: printClinic } = useGetClinicQuery(
    { clinicId: currentPatient?.preferred_clinic?.id || currentClinic?.id! },
    {
      skip: !(currentPatient?.preferred_clinic?.id || currentClinic?.id),
      refetchOnMountOrArgChange: true
    }
  );
  const clinicLogo = useMemo(
    () => printClinic?.logo || printClinic?.practice?.logo || "",
    [printClinic]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
    setIsTemplateUsed(false);
    setSelectedRecipient(null);
    setSubjectChanged(false);
    resetEmailBuilder();
  }, [onClose, reset, resetEmailBuilder]);

  // Reset form when modal opens with latest defaultValues
  // Only runs once when modal opens to prevent interrupting TinyMCE initialization
  const prevIsOpen = React.useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      reset(defaultValues);
      if (initialEmailDesignJson) {
        loadDesign(initialEmailDesignJson);
      } else if (openWithEmailBuilderTab) {
        setActiveTab(EmailEditorTab.EmailBuilder);
        setActiveDesign(null);
      }
    }
    prevIsOpen.current = isOpen;
  }, [
    isOpen,
    defaultValues,
    reset,
    initialEmailDesignJson,
    loadDesign,
    openWithEmailBuilderTab,
    setActiveTab,
    setActiveDesign
  ]);

  const onSubmit = async (formValue: EmailFormValues) => {
    const { files } = formValue;

    // Export HTML from email builder if in builder mode
    let emailBuilderData: { html: string; design: UnlayerDesign } | null = null;
    if (isEmailBuilder && isDragAndDropEmailEnabled) {
      try {
        emailBuilderData = await exportHtml();
      } catch (error) {
        // If export fails, continue with form value
        console.error("Failed to export email builder HTML:", error);
      }
    }

    const requestBody = getEmailRequestBody({
      ...formValue,
      content: emailBuilderData?.html ?? formValue.content,
      practiceLogo: clinicLogo,
      isTemplateUsed
    });

    if (isEmailBuilder && isDragAndDropEmailEnabled && emailBuilderData?.html) {
      const effectivePatient =
        formValue.patient ?? fetchedPatientForMerge ?? null;
      if (effectivePatient) {
        const clinicForMerge =
          effectivePatient.preferred_clinic ?? printClinic ?? null;
        requestBody.html = replaceMergeTagsWithValues(
          emailBuilderData.html,
          getMergeTagValuesMap(effectivePatient, clinicForMerge)
        );
      } else {
        requestBody.html = normalizeMergeTagsForSend(emailBuilderData.html);
      }
    }

    const response = await sendEmail({
      ...requestBody,
      attachments: selectedFiles?.map((file) => ({ box_file_id: file?.id }))
    });

    if ("error" in response) {
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
      return;
    }

    if (files.length) {
      const responseData = response.data;
      const ids = "id" in responseData ? [responseData.id] : responseData.ids;

      return attachEmailFiles({ ids, files, should_send: true }).then(
        (response) => {
          if ("error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setOpen(true));
          } else {
            dispatch(setCustomMessage("Email Sent"));
            dispatch(setOpen(true));
            handleClose();
          }
        }
      );
    } else {
      dispatch(setCustomMessage("Email Sent"));
      dispatch(setOpen(true));
      handleClose();
    }
  };

  const handleAfterOptionSelected = useCallback(
    (value: Array<Recipient | string>) => {
      const clinicName =
        currentPatient?.preferred_clinic?.display_name ??
        currentClinic?.display_name ??
        currentClinic?.practice?.name;

      const subject = getDefaultSubject(clinicName);

      if (
        !subjectChanged &&
        value.length === 1 &&
        typeof value[0] === "object"
      ) {
        const newRecipient = value[0];
        setSelectedRecipient(newRecipient);
        setSubjectChanged(true);
      } else if (!value.length) {
        setSelectedRecipient(null);
        setSubjectChanged(false);
      }

      setValue("subject", subject, { shouldDirty: true, shouldValidate: true });
    },
    [
      subjectChanged,
      currentClinic?.display_name,
      currentClinic?.practice?.name,
      setValue,
      currentPatient?.preferred_clinic?.display_name
    ]
  );

  const handleChangePatient = useCallback(
    (patient: Patient | null) => {
      if (patient && !selectedRecipient) {
        setSelectedRecipient(formatRecipient(patient));
      }
      setValue("patient", patient);
    },
    [setValue, selectedRecipient]
  );

  const disableMergeTags = !patientId;

  return (
    <ModalForm
      isOpen={isOpen}
      isSubmitting={isLoading}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      title={`New Email${
        selectedRecipient ? ` for ${selectedRecipient.name}` : ""
      }`}
      hideBackdrop={true}
      formName="product"
      control={control}
      submitIsDisabled={Boolean(uploadWarningMessage) || isLoading}
      submitButtonLabel={
        <Stack direction="row" gap={1}>
          <Box>Send</Box> <SendIcon sx={{ width: 16 }} />
        </Stack>
      }
      maxWidth="xl"
      sx={{ boxShadow: theme.shadows[3] }}
      dialogContentSx={{ p: 3, pb: 0, mb: -3, mt: 0 }}
      dialogActionsSx={{ px: 3, pb: 1 }}
    >
      <FormProvider {...formMethods}>
        <Collapse in={Boolean(uploadWarningMessage)}>
          <Alert severity="warning" sx={{ mb: 2, mt: -2 }}>
            {uploadWarningMessage}
          </Alert>
        </Collapse>
        <Grid container width="100%" gap={1.3} mt={-2}>
          <Grid item xs={12}>
            <RecipientsField
              control={control}
              name="recipients"
              label="To"
              afterOptionSelected={handleAfterOptionSelected}
            />
          </Grid>
          <Grid item xs={12}>
            <InputFormField
              name="subject"
              label="Subject"
              control={control}
              sx={{ ml: 0 }}
              inputProps={{ maxLength: EMAIL_SUBJECT_MAX_LENGTH }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <PatientFieldWithSearch
              label="Which patient is this email associated to"
              placeholder="Search Patient"
              control={control}
            />
          </Grid>

          <Grid item xs={12}>
            {disableMergeTags &&
              isOpen &&
              !sendEmailIsLoading &&
              !attachEmailFilesIsLoading && (
                <Typography
                  variant="body3"
                  mb={3}
                  width="100%"
                  sx={{ color: misc.warningText }}
                >
                  Please select a patient to activate templates and merge tags
                </Typography>
              )}
          </Grid>

          <Grid item xs={12} sx={{ minHeight: 364, mt: -2 }}>
            <EmailEditorTabs
              emailBuilderRef={emailBuilderRef}
              initialDesign={activeDesign}
              setActiveDesign={setActiveDesign}
              showTabs={isDragAndDropEmailEnabled}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              editorTabIsDisabled={editorTabIsDisabled}
              setEditorTabIsDisabled={setEditorTabIsDisabled}
              templateTypes={EMAIL_TEMPLATE_DESIGN_ONLY_TYPES}
              onExportFromBuilder={(html) => {
                setValue("content", html, { shouldDirty: true });
              }}
              mergeTags={EMAIL_BUILDER_MERGE_TAGS}
              editorContent={
                <WordProcessorField
                  name="content"
                  control={control}
                  placeholder=""
                  required={!isEmailBuilder}
                  templateTypes={EMAIL_TEMPLATE_WITH_DESIGN_TYPES}
                  mergeTagsPatientId={patientId}
                  allowImages={true}
                  maxImageSizeMB={4}
                  allowImageAutoUpload
                  allowTemplates={
                    !disableMergeTags && isDragAndDropEmailEnabled
                  }
                  allowMergeTags={!disableMergeTags}
                  allowInsertMergeTagsValues={!disableMergeTags}
                  onTemplateInserted={handleOnTemplateInserted}
                  allowToolbarAutohide={!initialEmailContent}
                />
              }
            />
          </Grid>

          {isApplyDefaultEmailTemplateEnabled && (
            <Grid item xs={12} mt={-2} mb={-2}>
              <FormControlLabel
                label="Apply Default Email Template"
                control={
                  <CheckboxFormField
                    name="isUseBackendEmailTemplate"
                    control={control}
                    testId="isUseBackendEmailTemplate"
                    sx={{ ml: 2 }}
                  />
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            {isUploadFilesFromEDocsEnabled ? (
              <FileUploadComputerAndEDocs
                name="files"
                setUploadWarningMessage={setUploadWarningMessage}
                patient={currentPatient}
                onPatientSelected={handleChangePatient}
              />
            ) : (
              <FileUpload
                name="files"
                label="Upload files (total size up to 10 MB)"
                fileSizeLimit={MAX_FILE_SIZE}
                totalFilesSizeLimit={MAX_FILE_SIZE}
                setUploadWarningMessage={setUploadWarningMessage}
              />
            )}
            <DisplayUploadSelectedFiles
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </ModalForm>
  );
}
